/**
 * Conversaciones (/conversaciones) — la bandeja comercial.
 *
 * Qué es: la lista de leads con los que hay una conversación abierta, ordenada
 * por quién espera respuesta. Qué NO es: un segundo Leads (no se cualifica ni
 * se edita nada aquí) ni un segundo Centro de acción (aquí no hay tareas,
 * pagos ni obra: solo conversaciones).
 *
 * De dónde salen los datos: de `GET /leads`, el mismo sitio que Leads. Los
 * mensajes no se cargan aquí —viven en la ficha del lead, que es donde está el
 * hilo entero y el compositor— así que esta pantalla no toca
 * `whatsapp_messages` ni abre una segunda copia de nada.
 *
 * Sobre el proveedor: hoy el canal real es WhatsApp a través de WACO, y así se
 * dice. Pero el concepto de producto es "conversación", no "WhatsApp": el día
 * que haya otro canal, entra aquí sin renombrar la sección.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowClockwise, ArrowRight, ChatCircleDots, ChatText, Clock,
  WarningCircle, WhatsappLogo,
} from "@phosphor-icons/react";
import AdminShell from "@/pages/admin/AdminShell";
import { Section } from "@/pages/Shell";
import { api, formatError } from "@/lib/api";
import { statusLabel } from "@/lib/leadStatuses";
import { relativeActivity } from "@/lib/relativeDate";
import {
  awaitingCount, conversationRowsFrom, previewLabel,
} from "@/lib/conversationsSelectors";

/** Todo lo que se toca llega a 44px. */
const TAP = "min-h-[44px]";

export default function ConversationsPage() {
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [onlyAwaiting, setOnlyAwaiting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(Array.isArray(data) ? data : []);
      setError("");
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => conversationRowsFrom(leads || []), [leads]);
  const awaiting = awaitingCount(rows);
  const visible = onlyAwaiting ? rows.filter((r) => r.awaiting) : rows;

  return (
    <AdminShell>
      <Section
        title="Conversaciones"
        subtitle="Con quién hay conversación abierta y quién está esperando respuesta."
        action={
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="conversations-reload"
            className={`dv-btn dv-btn-ghost ${TAP} inline-flex items-center gap-2 text-sm`}
          >
            <ArrowClockwise size={16} className={loading ? "animate-spin" : ""} />
            Actualizar
          </button>
        }
      >
        {/* El hilo se abre en la ficha del lead. Aquí van las dos pantallas
            que sostienen el canal y que ya existían sueltas en el menú. */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="conversations-toolbar">
          <button
            type="button"
            onClick={() => setOnlyAwaiting((v) => !v)}
            aria-pressed={onlyAwaiting}
            data-testid="conversations-filter-awaiting"
            className={`${TAP} inline-flex items-center gap-2 px-3 rounded-full text-sm border transition-colors ${
              onlyAwaiting
                ? "bg-stone-900 text-white border-stone-900 dark:bg-stone-100 dark:text-stone-900"
                : "bg-white text-stone-700 border-stone-300 hover:border-stone-500 dark:bg-stone-950 dark:text-stone-300 dark:border-stone-700"
            }`}
          >
            <WarningCircle size={14} weight={onlyAwaiting ? "fill" : "regular"} />
            Esperan respuesta
            <span className="tabular-nums">{awaiting}</span>
          </button>
          <div className="flex-1" />
          <Link
            to="/admin/message-templates"
            data-testid="conversations-link-templates"
            className={`${TAP} inline-flex items-center gap-2 px-3 rounded-full text-sm text-stone-600 dark:text-stone-300 border border-stone-200 dark:border-stone-800 hover:border-stone-400`}
          >
            <ChatText size={14} />
            Plantillas
          </Link>
          <Link
            to="/admin/whatsapp-pendientes"
            data-testid="conversations-link-pending"
            className={`${TAP} inline-flex items-center gap-2 px-3 rounded-full text-sm text-stone-600 dark:text-stone-300 border border-stone-200 dark:border-stone-800 hover:border-stone-400`}
          >
            <WhatsappLogo size={14} />
            Envíos sin resolver
          </Link>
        </div>

        {error && (
          <div className="dv-card text-sm text-red-700 dark:text-red-300" data-testid="conversations-error">
            {error}
          </div>
        )}

        {!error && loading && leads === null && (
          <div className="dv-card text-sm text-stone-400 animate-pulse" data-testid="conversations-loading">
            Cargando conversaciones…
          </div>
        )}

        {!error && leads !== null && visible.length === 0 && (
          <div className="dv-card text-center py-10" data-testid="conversations-empty">
            <ChatCircleDots size={22} className="mx-auto mb-2 text-stone-400" />
            <div className="text-sm text-stone-500 dark:text-stone-400">
              {onlyAwaiting
                ? "Nadie está esperando respuesta."
                : "Todavía no hay ninguna conversación."}
            </div>
          </div>
        )}

        {visible.length > 0 && (
          <ul className="space-y-2" data-testid="conversations-list">
            {visible.map((row) => <ConversationRow key={row.lead_id} row={row} />)}
          </ul>
        )}

        <p className="text-[11px] text-stone-400 mt-6 leading-relaxed" data-testid="conversations-note">
          El canal es WhatsApp (WACO). La conversación completa y el compositor
          están en la ficha del lead; aquí solo se ve quién escribió y cuándo.
          Del último mensaje se guarda el tipo, nunca el texto.
        </p>
      </Section>
    </AdminShell>
  );
}

function ConversationRow({ row }) {
  const when = relativeActivity(row.last_activity_at);
  return (
    <li>
      <Link
        to={row.href}
        data-testid={`conversation-${row.lead_id}`}
        className={`dv-card ${TAP} flex items-center gap-3 hover:border-stone-400 transition-colors`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="truncate text-sm text-stone-900 dark:text-stone-100">
              {row.name || "Sin nombre"}
            </span>
            {row.awaiting && (
              <span
                className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-500 text-white"
                data-testid={`conversation-awaiting-${row.lead_id}`}
              >
                Esperando respuesta
              </span>
            )}
            {row.unread > 0 && (
              <span
                className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-stone-200 text-stone-700 dark:bg-stone-800 dark:text-stone-300 tabular-nums"
                data-testid={`conversation-unread-${row.lead_id}`}
                aria-label={`${row.unread} mensajes sin leer`}
              >
                {row.unread > 99 ? "99+" : row.unread} sin leer
              </span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-stone-500 dark:text-stone-400">
            {row.status && <span>{statusLabel(row.status)}</span>}
            <span>·</span>
            <span>{row.direction === "in" ? "Escribió el lead" : "Escribimos nosotros"}</span>
            {when && (<><span>·</span><span>{when}</span></>)}
            {row.preview && (<><span>·</span><span>{previewLabel(row.preview)}</span></>)}
          </div>
          {/* La ventana de 24h decide qué se puede mandar. Se dice aquí para
              que nadie abra una conversación esperando poder escribir libre. */}
          <div className="mt-1 text-[11px] text-stone-400 flex items-center gap-1">
            <Clock size={11} />
            {row.window_open
              ? "Ventana de 24h abierta"
              : "Ventana cerrada · solo plantillas aprobadas"}
          </div>
        </div>
        <ArrowRight size={16} className="shrink-0 text-stone-400" />
      </Link>
    </li>
  );
}
